// src/components/common/PrivateRoute.tsx

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface PrivateRouteProps {
  children: React.ReactNode;
  role?: string;           // Single role (backward compatible)
  allowedRoles?: string[]; // Multiple roles (new)
  redirectTo?: string;     // Custom redirect path (optional)
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ 
  children, 
  role, 
  allowedRoles,
  redirectTo = '/login' 
}) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const userString = localStorage.getItem('user');
  
  // Check if user is logged in
  if (!token) {
    // Save the attempted location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If no user data in localStorage, redirect to login
  if (!userString) {
    localStorage.removeItem('token'); // Clear invalid token
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  let user;
  try {
    user = JSON.parse(userString);
  } catch (error) {
    // Invalid user data in localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check if user has required role (single role - backward compatibility)
  if (role && user.role !== role) {
    return <Navigate to="/" replace />;
  }

  // Check if user has any of the allowed roles
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  // Check if user account is active
  if (user.is_active === false) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return <Navigate to={redirectTo} state={{ from: location, error: 'Account deactivated' }} replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;